import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LotusIcon from "@/components/icons/LotusIcon";
import { ArrowUpDown, Sparkles, Sun, Moon, Flame, Heart, BookOpen, ExternalLink } from "lucide-react";
import guruNarayanImage from "@/assets/guru-narayan-goswami.jpg";
import guruBhartImage from "@/assets/guru-bhart-goswami.avif";
import caitanyaImage from "@/assets/caitanya-mahaprabhu.jpg";

const spiritualMasters = [
  {
    name: "Om Vishnu Pad Ashtotarshat Sri Srimad Bhakti Vedant Narayan Goswami Maharaj",
    shortName: "Srila Gurudeva",
    image: guruNarayanImage,
    website: "https://www.purebhakti.com/"
  },
  {
    name: "Om Vishnu Pad Ashtotarshat Sri Srimad Bhakti Vijnan Bharati Goswami Maharaj",
    shortName: "Srila Guru Maharaj",
    image: guruBhartImage,
    website: "https://www.visuddhacaitanyavani.com/"
  }
];

const ThePath = () => {
  return (
    <div className="min-h-screen" data-testid="the-path-page">
      <Header />
      
      {/* Hare Krishna Mantra Banner with Image */}
      <section className="pt-24 pb-6 bg-gradient-to-r from-accent/20 via-primary/10 to-accent/20">
        <div className="container">
          <div className="flex items-center justify-center gap-6 md:gap-10">
            {/* Left Side Mantra */}
            <div className="hidden md:block text-right flex-1">
              <p className="text-base lg:text-lg font-bold italic text-primary leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Hare Kṛṣṇa Hare Kṛṣṇa Kṛṣṇa Kṛṣṇa Hare Hare |<br />
                Hare Rām Hare Rām Rām Rām Hare Hare ||
              </p>
            </div>
            
            {/* Center - Caitanya Mahaprabhu Image */}
            <div className="flex-shrink-0">
              <div className="w-48 md:w-56 lg:w-64 rounded-2xl overflow-hidden border-4 border-accent/40 shadow-xl bg-white">
                <img 
                  src={caitanyaImage} 
                  alt="Sri Caitanya Mahaprabhu and Nityananda Prabhu" 
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            
            {/* Right Side Mantra */}
            <div className="hidden md:block text-left flex-1">
              <p className="text-base lg:text-lg font-bold italic text-primary leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Hare Kṛṣṇa Hare Kṛṣṇa Kṛṣṇa Kṛṣṇa Hare Hare |<br />
                Hare Rām Hare Rām Rām Rām Hare Hare ||
              </p>
            </div>
          </div>
          
          {/* Mobile - Mantra below image */}
          <div className="md:hidden mt-4 text-center">
            <p className="text-base font-bold italic text-primary leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Hare Kṛṣṇa Hare Kṛṣṇa Kṛṣṇa Kṛṣṇa Hare Hare |<br />
              Hare Rām Hare Rām Rām Rām Hare Hare ||
            </p>
          </div>
        </div>
      </section>
      
      {/* Gratitude to Spiritual Masters */}
      <section className="pt-8 pb-12 bg-gradient-to-b from-accent/10 via-primary/5 to-background">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4 italic font-bold">
              In Grateful Reverence to My Guiding Lights
            </h2>
            
            {/* Gratitude Statement */}
            <p className="text-foreground/80 leading-relaxed mb-10 max-w-2xl mx-auto font-medium">
              With deepest gratitude, I bow to my two spiritual masters, whose profound 
              wisdom and boundless compassion illuminate my path and inspire this journey 
              of transformation.
            </p>
            
            {/* Masters Grid */}
            <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {spiritualMasters.map((master, index) => (
                <div key={index} className="text-center">
                  {/* Photo */}
                  <div className="w-full max-w-xs mx-auto mb-4 rounded-2xl overflow-hidden border-4 border-accent/30 shadow-lg bg-white">
                    <img 
                      src={master.image} 
                      alt={master.shortName}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                  
                  {/* Name */}
                  <h3 className="text-sm md:text-base font-serif text-primary leading-snug mb-3 px-4 font-bold">
                    {master.name}
                  </h3>
                  
                  {/* Website Link */}
                  <div className="text-sm font-medium">
                    <span className="text-foreground/70">Visit Website: </span>
                    <a 
                      href={master.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-accent hover:text-primary transition-colors font-semibold"
                    >
                      {master.website.replace('https://www.', '').replace('/', '')}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Lotus Divider */}
            <div className="lotus-divider mt-10">
              <LotusIcon size={32} />
            </div>
          </div>
        </div>
      </section>
      
      {/* Hero Section */}
      <section className="pt-8 pb-12 bg-gradient-to-b from-primary/10 via-accent/5 to-background">
        <div className="container text-center max-w-4xl mx-auto">
          <LotusIcon size={60} className="mx-auto mb-6 text-accent" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-primary mb-6 font-bold">
            Spiritual Path
          </h1>
          <p className="text-lg md:text-xl text-foreground/80 leading-relaxed font-medium">
            Understanding the journey from the material realm to the spiritual realm — 
            the eternal wisdom of Vedantic philosophy.
          </p>
        </div>
      </section>

      {/* Main Verse Box */}
      <section className="py-8 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-primary/5 via-accent/10 to-primary/5 border border-primary/20 rounded-2xl p-6 md:p-8">
            <p className="text-sm font-bold text-primary mb-6 text-center tracking-widest uppercase">Sacred Verse</p>
            
            {/* Śvetāśvatara Upaniṣad 6.8 */}
            <div className="mb-10 text-center">
              <p className="text-base font-bold text-primary mb-4">Śvetāśvatara Upaniṣad 6.8</p>
              <div className="bg-white/40 rounded-xl p-5 mb-5 inline-block">
                <p className="font-serif text-lg md:text-xl text-primary leading-loose font-semibold" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                  न तस्य कार्यं करणं च विद्यते<br />
                  न तत्समश्चाभ्यधिकश्च दृश्यते<br />
                  परास्य शक्तिर्विविधैव श्रूयते<br />
                  स्वाभाविकी ज्ञानबलक्रिया च
                </p>
              </div>
              <p className="text-sm text-foreground/70 italic mb-5 leading-loose font-medium">
                na tasya kāryaṁ karaṇaṁ ca vidyate<br />
                na tat-samaś cābhyadhikaś ca dṛśyate<br />
                parāsya śaktir vividhaiva śrūyate<br />
                svābhāvikī jñāna‑bala‑kriyā ca
              </p>
              <div className="bg-white/50 rounded-xl p-5 mb-5 text-left">
                <p className="text-sm font-bold text-primary mb-2 text-center">Meaning:</p>
                <p className="text-sm text-foreground/80 leading-relaxed text-justify font-medium">
                  "The Supreme Lord has nothing to do, and no one is found to be equal to or greater than Him, for everything is done naturally and systematically by His multifarious energies." The Lord has nothing to do (na tasya kāryaṁ karaṇaṁ ca vidyate); whatever He does is His pastime. The Lord has no duty to perform to oblige anyone. The Supreme Lord has multipotencies, which act so perfectly that all consciousness, strength and activity are being directed solely by His will.
                </p>
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed mb-3 text-justify px-2 font-medium">
                In Śrī Caitanya‑caritāmṛta (Madhya 13.65), the līlā‑śakti is described as the Lord's internal potency that knows His desire (icchā) and makes all arrangements in the course of His pastimes.
              </p>
              <p className="text-sm text-foreground/80 leading-relaxed text-justify px-2 font-medium">
                The Lord may appear to "forget Himself" in the ecstasy of His līlā, but His līlā‑śakti, knowing His will, spontaneously arranges everything.
              </p>
            </div>

            {/* Śaktimān & Śakti */}
            <div className="mb-10 border-t border-primary/10 pt-8 text-center">
              <p className="text-base font-bold text-primary mb-4">Śaktimān & Śakti</p>
              <p className="text-sm text-foreground/80 leading-relaxed mb-3 text-justify px-2 font-medium">
                The word "parasya śaktir" clearly points to Śaktimān Himself — the Supreme Person — and not to an impersonal energy floating on its own.
              </p>
              <p className="text-sm text-foreground/80 leading-relaxed text-justify px-2 font-medium">
                Śakti is owned, known, and activated by the icchā of Śaktimān; without Śaktimān, śakti has no independent will or power to act.
              </p>
            </div>

            {/* Bhagavad‑gītā 9.10 */}
            <div className="mb-10 border-t border-primary/10 pt-8 text-center">
              <p className="text-base font-bold text-primary mb-4">Bhagavad‑gītā 9.10</p>
              <div className="bg-white/40 rounded-xl p-5 mb-5 inline-block">
                <p className="font-serif text-lg md:text-xl text-primary leading-loose font-semibold" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                  मयाध्यक्षेण प्रकृति: सूयते सचराचरम् ।<br />
                  हेतुनानेन कौन्तेय जगद्विपरिवर्तते ॥ १० ॥
                </p>
              </div>
              <p className="text-sm text-foreground/70 italic mb-5 leading-loose font-medium">
                mayādhyakṣeṇa prakṛtiḥ sūyate sa-carācaram<br />
                hetunānena kaunteya jagad viparivartate
              </p>
              <div className="bg-white/50 rounded-xl p-5">
                <p className="text-sm text-foreground/80 leading-relaxed text-justify font-medium">
                  "Under My direction, Prakriti (My energy) produces all moving and nonmoving beings. By this cause, the world is created and annihilated again and again."
                </p>
              </div>
            </div>

            {/* Conclusion */}
            <div className="mb-10 border-t border-primary/10 pt-8 text-center">
              <p className="text-base font-bold text-primary mb-4">Conclusion</p>
              <p className="text-sm text-foreground/80 leading-relaxed mb-3 text-justify px-2 font-medium">
                Thus, this verse resolves the Advaita‑dilemma: Śakti is real, but it is always dependent on Śaktimān.
              </p>
              <p className="text-sm text-foreground/80 leading-relaxed text-justify px-2 font-medium">
                For the Bhakti‑tradition, that Śaktimān is Kṛṣṇa Himself, the Supreme Person directing all activity through His will.
              </p>
            </div>

            {/* ŚB 1.3.28 */}
            <div className="border-t border-primary/10 pt-8 text-center">
              <p className="text-base font-bold text-primary mb-4">ŚB 1.3.28</p>
              <div className="bg-white/40 rounded-xl p-5 mb-5 inline-block">
                <p className="font-serif text-lg md:text-xl text-primary leading-loose font-semibold" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                  एते चांशकला: पुंस: कृष्णस्तु भगवान् स्वयम् ।<br />
                  इन्द्रारिव्याकुलं लोकं मृडयन्ति युगे युगे ॥ २८ ॥
                </p>
              </div>
              <p className="text-sm text-foreground/70 italic mb-5 leading-loose font-medium">
                ete cāṁśa-kalāḥ puṁsaḥ<br />
                kṛṣṇas tu bhagavān svayam<br />
                indrāri-vyākulaṁ lokaṁ<br />
                mṛḍayanti yuge yuge
              </p>
              <div className="bg-white/50 rounded-xl p-5">
                <p className="text-sm text-foreground/80 leading-relaxed text-justify font-medium">
                  All of the above-mentioned incarnations are either plenary portions or portions of the plenary portions of the Lord, but Lord Śrī Kṛṣṇa is the original Personality of Godhead. All of them appear on planets whenever there is a disturbance created by the atheists. The Lord incarnates to protect the theists.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Realms Diagram */}
      <section className="py-12 bg-sage-dark mandala-bg">
        <div className="container">
          <div className="text-center mb-10">
            <div className="lotus-divider">
              <LotusIcon size={32} />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              Spiritual Realm vs Material Realm
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-4 items-stretch justify-center max-w-5xl mx-auto">
            
            {/* Spiritual Realm - Left */}
            <div className="flex-1 min-w-[300px] bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-500 rounded-2xl p-6 relative overflow-hidden">
              {/* Decorative glow */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-200/30 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl font-bold text-green-700">Spiritual Realm</h3>
                    <p className="text-sm text-green-600 italic">Cit-Jala – Ocean of Pure Consciousness</p>
                  </div>
                </div>

                <div className="space-y-4 mt-6">
                  {/* Sandhini */}
                  <div className="bg-white/60 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Sun className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-bold text-green-800" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                          सन्धिनी (Sandhinī)
                        </p>
                        <p className="text-sm text-green-700 font-semibold">Sat (Existence)</p>
                      </div>
                    </div>
                    <p className="text-sm text-green-600 mt-3 font-medium">
                      The potency that sustains eternal existence in the spiritual realm
                    </p>
                  </div>

                  {/* Samvit */}
                  <div className="bg-white/60 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-bold text-green-800" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                          साम्वित् (Samvit)
                        </p>
                        <p className="text-sm text-green-700 font-semibold">Cit (Consciousness)</p>
                      </div>
                    </div>
                    <p className="text-sm text-green-600 mt-3 font-medium">
                      The potency of divine knowledge and all-pervading awareness
                    </p>
                  </div>

                  {/* Hladini */}
                  <div className="bg-white/60 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                        <Heart className="w-5 h-5 text-pink-600" />
                      </div>
                      <div>
                        <p className="font-bold text-green-800" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                          आह्लादिनी (Hlādinī)
                        </p>
                        <p className="text-sm text-green-700 font-semibold">Ānanda (Bliss)</p>
                      </div>
                    </div>
                    <p className="text-sm text-green-600 mt-3 font-medium">
                      The potency of supreme bliss — the essence of divine love
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-green-200">
                  <p className="text-sm text-green-700 italic text-center font-medium">
                    Krishna's internal energy — eternal, knowing, blissful.
                  </p>
                </div>
              </div>
            </div>

            {/* Center Arrow/Bridge */}
            <div className="flex lg:flex-col items-center justify-center py-4 lg:py-0 lg:px-2">
              <div className="flex flex-col items-center gap-2 bg-white/80 rounded-full px-4 py-6 shadow-lg border border-accent/30">
                <ArrowUpDown className="w-8 h-8 text-accent" />
                <span className="text-xs text-center text-foreground/70 font-semibold max-w-[80px]">
                  Movement of the Soul
                </span>
              </div>
            </div>

            {/* Material Realm - Right */}
            <div className="flex-1 min-w-[300px] bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-500 rounded-2xl p-6 relative overflow-hidden">
              {/* Decorative glow */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-orange-200/30 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                    <Moon className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl font-bold text-orange-700">Material Realm</h3>
                    <p className="text-sm text-orange-600 italic">Māyā-Śakti – Illusory Potency</p>
                  </div>
                </div>

                <div className="space-y-4 mt-6">
                  {/* Sattva */}
                  <div className="bg-white/60 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-white to-gray-100 border border-gray-300" />
                      </div>
                      <div>
                        <p className="font-bold text-orange-800" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                          सत्त्व (Sattva)
                        </p>
                        <p className="text-sm text-red-500 font-semibold">Goodness</p>
                      </div>
                    </div>
                    <p className="text-sm text-orange-700 mt-3 font-medium">
                      Clarity, harmony, and illumination
                    </p>
                  </div>

                  {/* Rajas */}
                  <div className="bg-white/60 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <Flame className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="font-bold text-orange-800" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                          राजस (Rajas)
                        </p>
                        <p className="text-sm text-red-500 font-semibold">Passion</p>
                      </div>
                    </div>
                    <p className="text-sm text-orange-700 mt-3 font-medium">
                      Activity, desire, and restless movement
                    </p>
                  </div>

                  {/* Tamas */}
                  <div className="bg-white/60 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                        <div className="w-5 h-5 rounded-full bg-gray-900" />
                      </div>
                      <div>
                        <p className="font-bold text-orange-800" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                          तामस (Tamas)
                        </p>
                        <p className="text-sm text-red-500 font-semibold">Ignorance</p>
                      </div>
                    </div>
                    <p className="text-sm text-orange-700 mt-3 font-medium">
                      Inertia, darkness, and delusion
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-orange-200">
                  <p className="text-sm text-orange-700 mb-2 font-medium">
                    <strong style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>शिव (Śiva)</strong> — masculine &nbsp;
                    <strong style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>माया (Māyā)</strong> — feminine
                  </p>
                  <p className="text-xs text-orange-600 italic text-center font-medium">
                    Governing the creation and sustenance of the material world.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Path Out of Maya Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="card-spiritual bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20">
              <div className="text-center mb-6">
                <LotusIcon size={40} className="mx-auto mb-4 text-accent" />
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">
                  Path Out of Māyā
                </h3>
              </div>

              <p className="text-foreground/80 text-center mb-6 leading-relaxed font-medium">
                We are already entangled in the material realm; the higher aim is to transcend 
                Māyā-Śakti and reach the spiritual realm of eternal consciousness.
              </p>

              <div className="bg-gradient-to-r from-accent/20 via-primary/10 to-accent/20 rounded-xl p-6 mb-6">
                <p className="text-xl md:text-2xl font-serif text-center text-primary italic font-semibold">
                  "Choice, inclination, and aspiration for the spiritual realm — 
                  is the inner compass of life."
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white/50 rounded-xl">
                  <p className="font-bold text-primary mb-1" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                    भक्तियोग
                  </p>
                  <p className="text-sm text-foreground/70 font-semibold">Bhakti Yoga</p>
                  <p className="text-xs text-foreground/80 mt-1 font-medium">Path of Devotion</p>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-xl">
                  <p className="font-bold text-primary mb-1" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                    श्रद्धा
                  </p>
                  <p className="text-sm text-foreground/70 font-semibold">Śraddhā</p>
                  <p className="text-xs text-foreground/80 mt-1 font-medium">Faith & Trust</p>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-xl">
                  <p className="font-bold text-primary mb-1" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                    वैराग्य
                  </p>
                  <p className="text-sm text-foreground/70 font-semibold">Vairāgya</p>
                  <p className="text-xs text-foreground/80 mt-1 font-medium">Detachment</p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-primary/10 text-center">
                <p className="text-sm text-foreground/80 font-medium">
                  <strong>The Means:</strong> Bhakti, surrender, discrimination, detachment (वैराग्य), 
                  and association with sādhus (spiritual practitioners).
                </p>
              </div>

              {/* NOI 8 Section */}
              <div className="mt-6 pt-6 border-t border-primary/10">
                <p className="text-base font-bold text-primary mb-4 text-center">NOI 8</p>
                <div className="bg-white/40 rounded-xl p-5 mb-4">
                  <p className="font-serif text-lg text-primary leading-loose text-center font-semibold" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                    तन्नामरूपचरितादिसुकीर्तनानुस्मृत्योः क्रमेण रसनामनसी नियोज्य ।<br />
                    तिष्ठन्व्रजे तदनुरागिजनानुगामी कालं नयेदखिलमित्युपदेशसारम् ॥ ८ ॥
                  </p>
                </div>
                <p className="text-sm text-foreground/70 italic mb-4 leading-loose text-center font-medium">
                  tan-nāma-rūpa-caritādi-sukīrtanānu-<br />
                  smṛtyoḥ krameṇa rasanā-manasī niyojya<br />
                  tiṣṭhan vraje tad-anurāgi-janānugāmī<br />
                  kālaṁ nayed akhilam ity upadeśa-sāram
                </p>
                <div className="bg-white/50 rounded-xl p-5">
                  <p className="text-sm font-bold text-primary mb-2 text-center">Translation</p>
                  <p className="text-sm text-foreground/80 leading-relaxed text-justify font-medium">
                    The essence of all advice is that one should utilize one's full time – twenty-four hours a day – in nicely chanting and remembering the Lord's divine name, transcendental form, qualities and eternal pastimes, thereby gradually engaging one's tongue and mind. In this way one should reside in Vraja [Goloka Vṛndāvana-dhāma] and serve Kṛṣṇa under the guidance of devotees. One should follow in the footsteps of the Lord's beloved devotees, who are deeply attached to His devotional service.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Summary */}
      <section className="py-12 bg-primary mandala-bg">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <p className="font-serif text-2xl md:text-3xl text-primary-foreground italic leading-relaxed font-semibold">
              "From the ocean of material existence, rise through devotion 
              to the eternal shore of spiritual consciousness."
            </p>
            <div className="mt-6 flex justify-center gap-4">
              <LotusIcon size={24} className="text-accent" />
              <LotusIcon size={24} className="text-accent" />
              <LotusIcon size={24} className="text-accent" />
            </div>
          </div>
        </div>
      </section>

      <Footer onStartQuiz={() => {}} />
    </div>
  );
};

export default ThePath;
